import { useCallback } from "react";
import { saveSentMessage } from "@/lib/api/messages";
import { useSaveInternalOpsMessage } from "@/lib/api/internal-ops-messaging";

type ClientSendbirdMessage = Parameters<typeof saveSentMessage>[0];

/**
 * Adapter hook that persists a Sendbird-sent message into
 * `internal_ops_messages` via the ops save endpoint. The `internalChannelId`
 * is passed per-call so callers can defer channel creation until first send.
 */
export function useOpsPersistMessage(onAfterSend?: () => void) {
  const saveMessage = useSaveInternalOpsMessage();

  return useCallback(
    async ({
      internalChannelId,
      sentMessage,
      file,
    }: {
      internalChannelId: string;
      sentMessage: ClientSendbirdMessage;
      file: File | null;
    }) => {
      const sdkId = String(
        (sentMessage as { messageId: string | number }).messageId,
      );
      const isFile = !!file;
      const sendbirdMessageType = (sentMessage as { messageType?: string })
        .messageType;
      const text = isFile
        ? ((sentMessage as { plainUrl?: string }).plainUrl ?? "")
        : ((sentMessage as { message?: string }).message ?? "");
      const customType = (
        sentMessage as { customType?: string }
      ).customType?.trim();
      await saveMessage.mutateAsync({
        channelId: internalChannelId,
        sdkId,
        message: text,
        messageType:
          sendbirdMessageType === "MESG" ||
          sendbirdMessageType === "ADMM" ||
          sendbirdMessageType === "FILE"
            ? sendbirdMessageType
            : isFile
              ? "FILE"
              : "MESG",
        customType:
          customType && customType.length > 0 ? customType : undefined,
        file: file ?? undefined,
      });
      onAfterSend?.();
    },
    [saveMessage, onAfterSend],
  );
}
